/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import baseColors from '@salesforce/retail-react-app/app/theme/foundations/colors';

const greyscale = {
  10: '#E8E8E8',
  20: '#D1D1D1',
  30: '#BBBABA',
  40: '#A4A3A3',
  50: '#8D8C8B',
  60: '#767674',
  70: '#5F5F5D',
  80: '#494846',
  90: '#32312F',
  100: '#1B1A18',
};

const primary = {
  darkLinen: '#8C683B',
  linen100: '#C6B5A1',
  linen50: '#E2DAD0',
  paleLinen: '#F7F5F0',
};

const secondary = {
  darkRed: '#91231A',
  desertRose: '#9A4958',
  green: '#393E36',
  newSaleRed: '#BF1619',
  summerRed: '#CD2A2D',
  surf: '#3D8CA6',
};

const background = {
  disabled: greyscale[30],
  linen03: '#D6D2C5',
  linen04: '#D1C6AA',
  primary: '#F7F5F0',
  secondary: '#F1EDE4',
};

const text = {
  disabled: greyscale[50],
  primary: greyscale[90],
  white: '#FFFFFF',
};

export default {
  ...baseColors,

  whiteAlpha: {
    ...baseColors.whiteAlpha,

    150: 'rgba(255, 255, 255, 0.05)',
    250: 'rgba(255, 255, 255, 0.1)',
    350: 'rgba(255, 255, 255, 0.20)', // 20% opacity
    450: 'rgba(255, 255, 255, 0.30)', // 30% opacity
    650: 'rgba(255, 255, 255, 0.50)', // 50% opacity
    700: 'rgba(255, 255, 255, 0.7)',
    900: 'rgba(255, 255, 255, 0.9)',
  },

  blackAlpha: {
    ...baseColors.blackAlpha,

    450: 'rgba(0, 0, 0, 0.30)', // 30% opacity
    546: 'rgba(0, 0, 0, 0.46)', // 46% opacity
    700: 'rgba(0, 0, 0, 0.7)',
    900: 'rgba(0, 0, 0, 0.9)',
  },

  // Map default grey color scheme to King Living greyscale
  gray: {
    50: '#E8E8E8',
    100: '#D1D1D1',
    200: '#BBBABA',
    300: '#A4A3A3',
    400: '#8D8C8B',
    500: '#767674',
    600: '#5F5F5D',
    700: '#494846',
    800: '#32312F',
    900: '#1B1A18',
  },

  grayAlpha: {
    10: 'rgba(27, 26, 24, 0.10)',
    20: 'rgba(27, 26, 24, 0.20)',
    30: 'rgba(27, 26, 24, 0.30)',
    40: 'rgba(27, 26, 24, 0.40)',
    50: 'rgba(27, 26, 24, 0.50)',
    60: 'rgba(27, 26, 24, 0.60)',
    70: 'rgba(27, 26, 24, 0.70)',
    80: 'rgba(27, 26, 24, 0.80)',
    90: 'rgba(27, 26, 24, 0.90)',
    100: 'rgba(27, 26, 24, 1.00)',
  },

  // Base colors
  primary,
  secondary,
  background,
  text,
  greyscale,

  // Semantic colors
  success: {
    standard: '#2D7831',
    background: '#E8F4E7',
    dark: '#1E5F35',
    text: text.primary,
  },

  info: {
    standard: '#0369A1',
    background: '#E0F2FE',
    dark: '#0C4A6E',
    text: text.primary,
  },

  warning: {
    standard: '#D97706',
    background: '#FEF3E2',
    dark: '#92400E',
    text: text.primary,
  },

  error: {
    standard: '#9D2A1E',
    background: '#FEF2F2',
    dark: '#81231A',
    text: text.primary,
  },

  /**
   * @deprecated
   */
  brandColors: {
    newSale: '#BF1619',
    primaryBG: '#F7F5F0', // Primary Pale Linen
    secondaryBG: '#F1EDE4',
    primaryDarkLinen: '#8C683B',
    primaryLinen15: 'rgba(198, 189, 161, 0.15)',
    primaryLinen100: '#C6B5A1', // Primary Linen 100
    charcoal: '#282829',
    darkCharcoal: '#333435',
    extraDarkCharcoal: '#2C2C2C',
    paleGrey: '#DDD',
    primaryText: '#32312F',
    linen03: '#D6D2C5',
    greys: '#797979',
    green: '#2D7831',
    greyscale: '#1B1A18',
    greyscale10: 'rgba(27, 26, 24, 0.10)',
    greyscale20: 'rgba(27, 26, 24, 0.20)',
    greyscale30: 'rgba(27, 26, 24, 0.30)',
    greyscale40: 'rgba(27, 26, 24, 0.40)',
    greyscale50: 'rgba(27, 26, 24, 0.50)',
    greyscale60: 'rgba(27, 26, 24, 0.60)',
    greyscale70: 'rgba(27, 26, 24, 0.70)',
    greyscale80: 'rgba(27, 26, 24, 0.80)',
    greyscale90: 'rgba(27, 26, 24, 0.90)', // WIP Greyscale 90 - primary text
    secondaryBGTranslucent15: 'rgba(198, 189, 161, 0.15)',
    error: '#9d2a1e', // Error color from Figma design
    disabledBG: '#e8e8e8', // Disabled background color from Figma design
    // Alert colors from Figma design
    errorBG: '#fef2f2', // Error background
    warning: '#d97706', // Warning border
    warningBG: '#fef3e2', // Warning background
    info: '#0369a1', // Info border
    infoBG: '#e0f2fe', // Info background
    success: '#2d7831', // Success border (using existing green)
    successBG: '#e8f4e7', // Success background
  },
};
